// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// THE TAXONOMY, DRILLED — an icicle, not a treemap.
//
// The ACCC's categories are a real two-level hierarchy (Kids' toys → Learning
// and activity toys), and the question this view answers is "what part of the
// shelf gets recalled, and how does it break down". A treemap would size the
// leaves correctly and destroy the level structure; an icicle puts depth on one
// axis so containment stays visible and every label sits on its own row.
//
// The size toggle is the point of the view rather than a decoration: sizing by
// RECALL COUNT and sizing by DISTINCT SUPPLIERS answer different questions, and
// the gap between them separates "one company recalled forty products" from
// "forty companies had the same problem".

import { getBundle } from '../data';
import { esc, num } from '../format';
import { navigate } from '../router';
import { chartFrame, labelChart, markTip, svgEl } from '../components/svg';
import type { TaxonomyTerm } from '../types';
import { icicleHeight, icicleLayout, type IcicleInput } from '../utils/icicleLayout';

type SizeMode = 'recalls' | 'suppliers';

/**
 * "Compulsory recall" is a top-level facet in the register's taxonomy, but it
 * is a recall TYPE, not a place on a shelf. Including it in a chart about what
 * kind of product gets recalled would put a legal category alongside "Kids'
 * toys" and invite a comparison that means nothing. It is excluded here, said
 * so on the page, and remains fully searchable.
 */
const NON_CATEGORY_TERMS = new Set([40159]);

export function renderTaxonomy(host: HTMLElement): void {
  const { terms, meta } = getBundle();
  let mode: SizeMode = 'recalls';

  const excluded = terms.filter((t) => NON_CATEGORY_TERMS.has(t.id));

  host.innerHTML = `
    <div class="view-head">
      <h2>What gets recalled</h2>
      <p>The ACCC's own product taxonomy — ${meta.taxonomyTerms} terms across two levels — sized by how many
      recalls land in each. A recall usually carries several terms, so the parts of a level can add to more
      than the whole; the tooltip gives each term's own count.</p>
    </div>
    <div class="controls">
      <div class="control">
        <span class="field-label" id="size-label">Size blocks by</span>
        <div class="row" role="group" aria-labelledby="size-label" style="gap:var(--space-xs)">
          <button type="button" class="btn btn-sm is-active" data-mode="recalls" aria-pressed="true">Recalls</button>
          <button type="button" class="btn btn-sm" data-mode="suppliers" aria-pressed="false">Distinct suppliers</button>
        </div>
      </div>
    </div>
    <div id="icicle"></div>
    <div class="note caveat">
      Category labels are applied using <strong>today's</strong> taxonomy, including to recalls from decades
      ago, so these are the ACCC's current view of its own history.
      ${excluded.length ? `“${esc(excluded[0].name)}” (${num(excluded[0].count)} recalls) is excluded from this chart — it describes how a recall was compelled, not what kind of product it was.` : ''}
    </div>
  `;

  const mountHost = host.querySelector<HTMLElement>('#icicle')!;

  const draw = () => {
    mountHost.innerHTML = '';
    mountHost.append(icicle(terms, mode));
  };

  for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-mode]')) {
    btn.addEventListener('click', () => {
      mode = btn.dataset.mode as SizeMode;
      for (const b of host.querySelectorAll<HTMLButtonElement>('[data-mode]')) {
        const on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', String(on));
      }
      draw();
    });
  }

  draw();
}

const ROW_H = 30;
const GAP = 2;

function icicle(terms: TaxonomyTerm[], mode: SizeMode): HTMLElement {
  const value = (t: TaxonomyTerm) => (mode === 'recalls' ? t.count : t.suppliers);

  const tops = terms
    .filter((t) => t.parent == null && !NON_CATEGORY_TERMS.has(t.id))
    .sort((a, b) => value(b) - value(a));

  const childrenOf = new Map<number, TaxonomyTerm[]>();
  for (const t of terms) {
    if (t.parent == null) continue;
    if (!childrenOf.has(t.parent)) childrenOf.set(t.parent, []);
    childrenOf.get(t.parent)!.push(t);
  }

  const groups: IcicleInput[] = tops.map((t) => ({
    id: t.id,
    value: value(t),
    children: (childrenOf.get(t.id) ?? [])
      .filter((k) => value(k) > 0)
      .sort((a, b) => value(b) - value(a))
      .map((k) => ({ id: k.id, value: value(k) })),
  }));

  const W = 900;
  const LAYOUT = { width: W, rowHeight: ROW_H, gap: GAP, padLeft: 4, padTop: 8 };
  const rects = icicleLayout(groups, LAYOUT);
  const H = icicleHeight(groups, LAYOUT);

  const { figure, mount } = chartFrame({
    title: mode === 'recalls' ? 'Recalls by product category' : 'Distinct suppliers by product category',
    subtitle: mode === 'recalls'
      ? 'Width is the number of recalls. Click any block to search that category.'
      : 'Width is the number of different companies. Compare with the recall view: a wide block here and a narrow one there means many companies had the same problem.',
    scroll: true,
    minWidth: 640,
  });

  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, width: W, height: H, preserveAspectRatio: 'xMinYMin meet' });
  svg.style.minWidth = '640px';
  labelChart(svg, mode === 'recalls' ? 'Product categories sized by recall count' : 'Product categories sized by number of distinct suppliers',
    'A two-level icicle: each top-level category is a bar, with its sub-categories below it.');

  const byId = new Map(terms.map((t) => [t.id, t]));
  const parentOf = new Map(rects.map((r) => [r.id, r.parent]));

  /*
   * A block narrower than MIN_INTERACTIVE_W is drawn but NOT made a focusable
   * control. 73 of 118 blocks were sub-24px click targets and the smallest was
   * 0.3 CSS px — a tab stop a mouse cannot hit and a screen reader announces as
   * a button. They stay visible (they are part of the distribution) and remain
   * reachable through search and through the parent's tooltip.
   */
  // In viewBox user units. The chart is 900 units wide inside a 640px-minimum
  // scroller, so this is ~13 CSS px at the narrowest rendering.
  const MIN_INTERACTIVE_W = 18;
  let hiddenTargets = 0;
  for (const rect of rects) {
    const term = byId.get(rect.id);
    if (!term || rect.w <= 0) continue;
    const topId = rect.depth === 0 ? rect.id : (parentOf.get(rect.id) ?? rect.id);
    const interactive = rect.w >= MIN_INTERACTIVE_W;
    if (!interactive) hiddenTargets++;
    svg.append(block(term, rect.x, rect.y, rect.w, rect.h, colourFor(topId), rect.depth === 0, mode, interactive));
  }
  if (hiddenTargets) {
    const note = document.createElement('p');
    note.className = 'small muted';
    note.style.marginTop = 'var(--space-sm)';
    note.textContent = `${hiddenTargets} sub-categories are too small to label or click at this size — hover the parent block for its full breakdown, or use Search.`;
    mount.append(note);
  }

  mount.append(svg);
  return figure;
}

const PALETTE = ['#0f5c63', '#6d4aa8', '#8a6d3b', '#15654a', '#1d4e89', '#7a1f4b', '#a3651b', '#4b6b2a',
  '#3f5d7a', '#8f4a2a', '#4a5568', '#2b6a5c', '#7a5c1f', '#5a3a7a', '#6b2f2f'];
function colourFor(id: number): string {
  return PALETTE[id % PALETTE.length];
}

/** Mix a hex colour towards white by `t`, so dark ink stays legible on it. */
function lighten(hex: string, t: number): string {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16))
    .map((v) => Math.round(v + (255 - v) * t));
  return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

function block(
  term: TaxonomyTerm, x: number, y: number, w: number, h: number,
  colour: string, isTop: boolean, mode: SizeMode, interactive = true,
): SVGGElement {
  const g = interactive
    ? svgEl('g', { class: 'mark', role: 'button', tabindex: '0', style: 'cursor:pointer' })
    : svgEl('g', { class: 'mark', 'aria-hidden': 'true' });
  /*
   * Children are drawn with a LIGHTENED fill at full opacity rather than the
   * parent colour at 60% opacity, and labelled in the ink colour.
   *
   * White text on a 0.6-opacity fill measured 2.34:1 to 3.51:1 — not one of the
   * 104 sub-category labels reached AA. Opacity was doing double duty as a
   * visual hierarchy cue and quietly destroying legibility; lightening the fill
   * keeps the hierarchy and lets the label be dark.
   */
  const rect = svgEl('rect', {
    x: x.toFixed(2), y, width: w.toFixed(2), height: h, rx: 3,
    fill: isTop ? colour : lighten(colour, 0.72),
    stroke: 'var(--bg-base)', 'stroke-width': 1,
  });
  g.append(rect);

  // Only label a block that can actually hold its label. An overflowing label
  // in an SVG does not ellipsis — it simply prints across its neighbours.
  const approxChar = 6.6;
  const fits = Math.floor((w - 10) / approxChar);
  if (fits >= 4) {
    const text = svgEl('text', {
      x: (x + 6).toFixed(2), y: y + h / 2 + 4,
      fill: isTop ? '#fff' : 'var(--text-primary)',
      'font-size': isTop ? 12.5 : 11.5,
      'font-weight': isTop ? 600 : 400, 'pointer-events': 'none',
    });
    text.textContent = term.name.length > fits ? `${term.name.slice(0, Math.max(1, fits - 1))}…` : term.name;
    g.append(text);
  }

  const tipText = `${term.name}\n${num(term.count)} recall${term.count === 1 ? '' : 's'}\n${num(term.suppliers)} distinct supplier${term.suppliers === 1 ? '' : 's'}`
    + (mode === 'suppliers' && term.suppliers > 0 ? `\n${(term.count / term.suppliers).toFixed(1)} recalls per supplier` : '');
  markTip(g, tipText, `${term.name}: ${num(term.count)} recalls, ${num(term.suppliers)} suppliers. Show these recalls.`);

  const go = () => navigate('search', { c: term.id });
  g.addEventListener('click', go);
  g.addEventListener('keydown', (e) => {
    const ev = e as KeyboardEvent;
    if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); go(); }
  });
  return g as SVGGElement;
}
